import { Component, ChangeDetectionStrategy } from '@angular/core';

@Component({
  selector: 'app-feature-guide',
  template: `
    <nz-modal
      [(nzVisible)]="visible"
      nzTitle="🚀 New Features Guide"
      [nzFooter]="null"
      nzWidth="800px"
      (nzOnCancel)="close()">
      
      <div class="feature-guide-content">
        <nz-steps nzCurrent="0" nzDirection="vertical" nzSize="small">
          
          <!-- Floating Inspector -->
          <nz-step nzTitle="Floating Inspector" nzIcon="control">
            <ng-template #nzStepContent>
              <div class="feature-description">
                <p><strong>Smart contextual property panel that appears near selected components.</strong></p>
                <ul>
                  <li>🎯 <strong>How to use:</strong> Click any component on the canvas</li>
                  <li>📍 <strong>Auto-positioning:</strong> Inspector appears near the selected component</li>
                  <li>🔍 <strong>Search properties:</strong> Use the search bar to find specific properties</li>
                  <li>📱 <strong>Responsive:</strong> Adapts to screen size (modal on mobile, floating on desktop)</li>
                </ul>
                <nz-tag nzColor="blue">Try it: Click a component to see the floating inspector!</nz-tag>
              </div>
            </ng-template>
          </nz-step>

          <!-- Inline Editing -->
          <nz-step nzTitle="Inline Canvas Editing" nzIcon="edit">
            <ng-template #nzStepContent>
              <div class="feature-description">
                <p><strong>Quick property editing directly on the canvas.</strong></p>
                <ul>
                  <li>⚡ <strong>How to use:</strong> Double-click any component on the canvas</li>
                  <li>🎛️ <strong>Quick properties:</strong> Edit essential properties without opening full inspector</li>
                  <li>⌨️ <strong>Keyboard shortcuts:</strong> Ctrl+Enter to open full editor, Escape to close</li>
                  <li>🎨 <strong>Color picker:</strong> Change colors instantly with visual feedback</li>
                </ul>
                <nz-tag nzColor="green">Try it: Double-click any component for quick editing!</nz-tag>
              </div>
            </ng-template>
          </nz-step>

          <!-- Property Presets -->
          <nz-step nzTitle="Property Presets" nzIcon="star">
            <ng-template #nzStepContent>
              <div class="feature-description">
                <p><strong>Pre-configured styles and settings for quick component setup.</strong></p>
                <ul>
                  <li>🎨 <strong>Built-in presets:</strong> Primary buttons, email fields, striped tables, dark theme</li>
                  <li>💾 <strong>Custom presets:</strong> Save your own configurations for reuse</li>
                  <li>🔥 <strong>Popular presets:</strong> See most-used configurations</li>
                  <li>⭐ <strong>How to use:</strong> Select component → Click star icon in toolbar → Choose preset</li>
                </ul>
                <nz-tag nzColor="orange">Try it: Select a component and click the star (⭐) button!</nz-tag>
              </div>
            </ng-template>
          </nz-step>

          <!-- AI Suggestions -->
          <nz-step nzTitle="AI-Powered Suggestions" nzIcon="bulb">
            <ng-template #nzStepContent>
              <div class="feature-description">
                <p><strong>Intelligent recommendations for improving your components.</strong></p>
                <ul>
                  <li>♿ <strong>Accessibility:</strong> Missing labels, color contrast issues, keyboard navigation</li>
                  <li>🎯 <strong>UX improvements:</strong> Better button text, helpful placeholders, loading states</li>
                  <li>✅ <strong>Validation:</strong> Email patterns, number limits, required fields</li>
                  <li>💡 <strong>How to use:</strong> Select component → Click bulb icon in toolbar → Apply suggestions</li>
                </ul>
                <nz-tag nzColor="purple">Try it: Select a component and click the bulb (💡) button!</nz-tag>
              </div>
            </ng-template>
          </nz-step>

          <!-- Drag & Drop -->
          <nz-step nzTitle="Drag & Drop Reordering" nzIcon="drag">
            <ng-template #nzStepContent>
              <div class="feature-description">
                <p><strong>Customize property panel organization to match your workflow.</strong></p>
                <ul>
                  <li>📋 <strong>Reorder groups:</strong> Drag property groups to your preferred order</li>
                  <li>🔧 <strong>Reorder properties:</strong> Drag individual properties within groups</li>
                  <li>💾 <strong>Persistent:</strong> Your custom order is saved per component type</li>
                  <li>🔄 <strong>Reset:</strong> Restore default order anytime</li>
                </ul>
                <nz-tag nzColor="cyan">Try it: Open floating inspector and drag property groups!</nz-tag>
              </div>
            </ng-template>
          </nz-step>

          <!-- Performance -->
          <nz-step nzTitle="Performance Optimizations" nzIcon="rocket">
            <ng-template #nzStepContent>
              <div class="feature-description">
                <p><strong>Smooth performance even with complex forms.</strong></p>
                <ul>
                  <li>⚡ <strong>Virtual scrolling:</strong> Handle hundreds of properties without lag</li>
                  <li>🎭 <strong>Smart animations:</strong> Respects reduced motion preferences</li>
                  <li>🧠 <strong>Intelligent loading:</strong> Properties load only when needed</li>
                  <li>📱 <strong>Responsive:</strong> Optimized for all screen sizes</li>
                </ul>
                <nz-tag nzColor="red">Automatic: Performance optimizations work behind the scenes!</nz-tag>
              </div>
            </ng-template>
          </nz-step>

        </nz-steps>

        <div class="guide-footer">
          <nz-alert 
            nzType="info" 
            nzMessage="Pro Tip" 
            nzDescription="All features work together! Use floating inspector for detailed editing, inline editing for quick changes, presets for common styles, and AI suggestions for improvements."
            nzShowIcon>
          </nz-alert>
        </div>
      </div>
    </nz-modal>
  `,
  styleUrls: ['./feature-guide.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FeatureGuideComponent {
  visible = false;

  show() {
    this.visible = true;
  }

  close() {
    this.visible = false;
  }
}
